import { useCallback } from "react";
import { type TabData } from "@utils/dashboard-storage";
import { TabsState } from "./reducer";
import { useTabsState } from "./use-tabs-state";
import { useTabApiSync } from "./use-tab-api-sync";
import { isTemp } from "./utils";
import { deleteGroup, getGroupMembers } from "./group-utils";

export const useTabDeletion = (
  state: TabsState,
  actions: ReturnType<typeof useTabsState>["actions"],
  api: ReturnType<typeof useTabApiSync>,
  onGroupDissolved: (nextTabs: TabData[], previousTabs: TabData[]) => void,
) => {
  const { localTabs, deleteConfirmation, groupDeleteConfirmation } = state;
  const {
    openDeleteConfirm,
    closeDeleteConfirm,
    openGroupDeleteConfirm,
    closeGroupDeleteConfirm,
  } = actions;

  const handleDeleteClick = useCallback(
    (tabId: string) => {
      if (localTabs.length <= 1) return;
      const tab = localTabs.find((t) => t.id === tabId);
      if (!tab) return;
      // A group dropping to 1 remaining member will be dissolved on delete.
      const affectedGroupName =
        tab.groupName && getGroupMembers(localTabs, tab.groupName).length === 2
          ? tab.groupName
          : null;
      // temporary tabs => hasContent false, otherwise determined by query
      openDeleteConfirm(tabId, tab.label, isTemp(tabId) ? false : false, affectedGroupName);
    },
    [localTabs, openDeleteConfirm],
  );

  const confirmDelete = useCallback(
    async (forceDelete: boolean) => {
      try {
        await api.confirmDelete(forceDelete);
        closeDeleteConfirm();
      } catch (error) {
        console.error("Error deleting tab:", error);
        closeDeleteConfirm();
      }
    },
    [api, closeDeleteConfirm],
  );

  const cancelDelete = useCallback(() => {
    closeDeleteConfirm();
  }, [closeDeleteConfirm]);

  const handleDeleteGroupClick = useCallback(
    (groupName: string) => openGroupDeleteConfirm(groupName),
    [openGroupDeleteConfirm],
  );

  const confirmDeleteGroup = useCallback(() => {
    if (!groupDeleteConfirmation) return;
    const before = localTabs;
    const next = deleteGroup(before, groupDeleteConfirmation.groupName);
    actions.setLocalTabs(next);
    closeGroupDeleteConfirm();
    onGroupDissolved(next, before);
  }, [groupDeleteConfirmation, localTabs, actions, closeGroupDeleteConfirm, onGroupDissolved]);

  const cancelDeleteGroup = useCallback(() => {
    closeGroupDeleteConfirm();
  }, [closeGroupDeleteConfirm]);

  return {
    deleteConfirmation,
    groupDeleteConfirmation,
    handleDeleteClick,
    confirmDelete,
    cancelDelete,
    handleDeleteGroupClick,
    confirmDeleteGroup,
    cancelDeleteGroup,
  } as const;
};
